import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Trophy, Users, TrendingUp, Edit, Loader2, Eye, Heart, 
  MessageCircle, FileText, CheckCircle, XCircle, Clock, 
  Mail, MapPin, Calendar, Plus, BarChart3, Award, Target, 
  Briefcase, RefreshCw, Trash2, ArrowLeft, ChevronRight
} from 'lucide-react';
import { dashboardAPI, trialAPI, jobsAPI, enquiriesAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDashboardRefresh } from '../../hooks/useDashboardRefresh';
import EngagementAnalytics from '../EngagementAnalytics';

// Import extracted modal components
import {
  JobApplicationDetailModal,
  TrialApplicationDetailModal,
  EditJobModal,
  EnquiryDetailModal,
  EditStatsModal
} from './club';

const ClubDashboard = () => {
  // Core state
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editableStats, setEditableStats] = useState({});
  
  // Trial Applications state
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [trialsList, setTrialsList] = useState([]);
  const [selectedTrialTab, setSelectedTrialTab] = useState('all');
  const [selectedTrialApp, setSelectedTrialApp] = useState(null);
  const [trialAppDetailModal, setTrialAppDetailModal] = useState(false);
  
  // Enquiries state
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [enquiryFilter, setEnquiryFilter] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [enquiryDetailModal, setEnquiryDetailModal] = useState(false);
  
  // Job Applications state
  const [jobApplications, setJobApplications] = useState([]);
  const [loadingJobApplications, setLoadingJobApplications] = useState(false);
  const [jobAppFilter, setJobAppFilter] = useState('');
  const [jobAppStats, setJobAppStats] = useState({ total: 0, applied: 0, shortlisted: 0, accepted: 0, rejected: 0 });
  const [selectedJobApp, setSelectedJobApp] = useState(null);
  const [jobAppDetailModal, setJobAppDetailModal] = useState(false);
  
  // Job Management state
  const [myJobs, setMyJobs] = useState([]);
  const [loadingMyJobs, setLoadingMyJobs] = useState(false);
  const [jobFilter, setJobFilter] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [editJobModal, setEditJobModal] = useState(false);
  
  // Stats & Engagement modals
  const [editStatsModal, setEditStatsModal] = useState(false);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Get active section from URL or default to 'overview'
  const activeSection = searchParams.get('section') || 'overview';
  
  const setActiveSection = (section) => {
    if (section === 'overview') {
      searchParams.delete('section');
    } else {
      searchParams.set('section', section);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Auto-refresh functionality
  const refreshCallback = useCallback(async () => {
    await Promise.all([
      loadDashboard(true),
      loadApplicants(true),
      loadEnquiries(true),
      loadJobApplications(true),
      loadMyJobs(true)
    ]);
  }, [filterStatus, enquiryFilter, jobAppFilter]);

  const { refreshing, lastRefreshTime, handleManualRefresh } = useDashboardRefresh(refreshCallback, 10000);

  // Initial load - parallel calls for faster loading
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadDashboard(true),
          loadApplicants(true),
          loadEnquiries(true),
          loadJobApplications(true),
          loadMyJobs(true)
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (enquiryFilter) loadEnquiries();
  }, [enquiryFilter]);

  useEffect(() => {
    loadJobApplications();
  }, [jobAppFilter]);

  useEffect(() => {
    if (filterStatus || selectedTrialTab) loadApplicants();
  }, [filterStatus, selectedTrialTab]);

  useEffect(() => {
    if (activeSection === 'jobmanagement') loadMyJobs();
  }, [activeSection, jobFilter]);

  // Data loading functions
  const loadDashboard = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await dashboardAPI.getClubDashboard();
      setDashboardData(response.data);
      setEditableStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      if (!silent) toast.error('Failed to load dashboard data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadApplicants = async (silent = false) => {
    try {
      if (!silent) setLoadingApplicants(true);
      const params = {};
      if (selectedTrialTab && selectedTrialTab !== 'all') params.trial_id = selectedTrialTab;
      if (filterStatus) params.status = filterStatus;
      
      const response = await dashboardAPI.getTrialApplicants(params);
      setApplicants(response.data.applicants || []);
      setTrialsList(response.data.trials || []);
    } catch (error) {
      console.error('Failed to load applicants:', error);
    } finally {
      if (!silent) setLoadingApplicants(false);
    }
  };

  const loadEnquiries = async (silent = false) => {
    try {
      if (!silent) setLoadingEnquiries(true);
      const statusFilter = enquiryFilter !== 'all' ? enquiryFilter : null;
      const response = await enquiriesAPI.getReceivedEnquiries(statusFilter);
      setEnquiries(response.data.enquiries || []);
    } catch (error) {
      console.error('Failed to load enquiries:', error);
      if (!silent) toast.error('Failed to load enquiries');
    } finally {
      if (!silent) setLoadingEnquiries(false);
    }
  };

  const loadJobApplications = async (silent = false) => {
    try {
      if (!silent) setLoadingJobApplications(true);
      const response = await jobsAPI.getReceivedJobApplications(jobAppFilter || null);
      setJobApplications(response.data.applications || []);
      setJobAppStats(response.data.stats || { total: 0, applied: 0, shortlisted: 0, accepted: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to load job applications:', error);
      if (!silent) toast.error('Failed to load job applications');
    } finally {
      if (!silent) setLoadingJobApplications(false);
    }
  };

  const loadMyJobs = async (silent = false) => {
    try {
      if (!silent) setLoadingMyJobs(true);
      const response = await jobsAPI.getMyJobs(jobFilter || null);
      setMyJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      if (!silent) toast.error('Failed to load jobs');
    } finally {
      if (!silent) setLoadingMyJobs(false);
    }
  };

  // Action handlers
  const handleTrialAppStatusUpdate = async (applicationId, status) => {
    try {
      await dashboardAPI.updateTrialApplicationStatus(applicationId, status);
      const statusMessages = {
        'shortlisted': 'Application shortlisted successfully',
        'accepted': 'Application accepted successfully',
        'rejected': 'Application rejected',
        'applied': 'Application status reset to applied'
      };
      toast.success(statusMessages[status] || `Application ${status}`);
      loadApplicants(true);
      if (selectedTrialApp && selectedTrialApp._id === applicationId) {
        setSelectedTrialApp({ ...selectedTrialApp, status });
      }
    } catch (error) {
      console.error('Failed to update application status:', error);
      toast.error('Failed to update application status');
    }
  };

  const handleJobAppStatusUpdate = async (applicationId, status) => {
    try {
      await jobsAPI.updateJobApplicationStatus(applicationId, status);
      const statusMessages = {
        'shortlisted': 'Application shortlisted successfully',
        'accepted': 'Application accepted successfully',
        'rejected': 'Application rejected',
        'applied': 'Application status reset to applied'
      };
      toast.success(statusMessages[status] || `Application ${status}`);
      loadJobApplications(true);
      if (selectedJobApp && selectedJobApp._id === applicationId) {
        setSelectedJobApp({ ...selectedJobApp, status });
      }
    } catch (error) {
      console.error('Failed to update application status:', error);
      toast.error('Failed to update application status');
    }
  };

  const handleDeleteTrial = async (trialId, e) => {
    e.stopPropagation(); // Prevent navigation to trial detail
    if (!window.confirm('Are you sure you want to delete this trial? This action cannot be undone.')) return;
    try {
      await trialAPI.deleteTrial(trialId);
      toast.success('Trial deleted successfully');
      // Refresh dashboard data
      loadDashboardData();
    } catch (error) {
      console.error('Failed to delete trial:', error);
      toast.error('Failed to delete trial');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) return;
    try {
      await jobsAPI.deleteJob(jobId);
      toast.success('Job deleted successfully');
      loadMyJobs(true);
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleUpdateJob = async (jobId, formData) => {
    try {
      await jobsAPI.updateJob(jobId, formData);
      toast.success('Job updated successfully');
      setEditJobModal(false);
      setSelectedJob(null);
      loadMyJobs(true);
    } catch (error) {
      console.error('Failed to update job:', error);
      toast.error('Failed to update job');
    }
  };

  const handleEnquiryStatusUpdate = async (enquiryId, status) => {
    try {
      await enquiriesAPI.updateStatus(enquiryId, status);
      toast.success(`Enquiry marked as ${status}`);
      if (selectedEnquiry && selectedEnquiry._id === enquiryId) {
        setSelectedEnquiry({ ...selectedEnquiry, status });
      }
      loadEnquiries(true);
    } catch (error) {
      console.error('Failed to update enquiry status:', error);
      toast.error('Failed to update enquiry status');
    }
  };

  const handleMarkEnquiryRead = async (enquiryId) => {
    try {
      await enquiriesAPI.markAsRead(enquiryId);
      loadEnquiries(true);
    } catch (error) {
      console.error('Failed to mark enquiry as read:', error);
    }
  };

  const handleSaveStats = async (stats) => {
    try {
      await dashboardAPI.updateStats(stats);
      toast.success('Stats updated successfully!');
      setEditStatsModal(false);
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update stats');
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'applied': 'badge-primary',
      'pending': 'badge-primary',
      'shortlisted': 'badge-warning',
      'accepted': 'badge-success',
      'rejected': 'badge-danger',
      'open': 'badge-success',
      'closed': 'badge-danger',
      'responded': 'badge-success'
    };
    return statusMap[status?.toLowerCase()] || 'badge-neutral';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const trials = dashboardData?.trials || {};
  const applications = dashboardData?.applications || {};
  const analytics = dashboardData?.analytics || {};

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-x-hidden" data-testid="club-dashboard">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Club Dashboard</h1>
              <Button
                onClick={async () => {
                  await handleManualRefresh();
                  toast.success('Dashboard refreshed!');
                }}
                variant="ghost"
                size="sm"
                disabled={refreshing}
                className="h-8 w-8 p-0 rounded-full"
                title="Refresh dashboard"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Manage trials, discover talent, and grow your club
              {lastRefreshTime && (
                <span className="ml-2 text-xs opacity-70">
                  • Updated {lastRefreshTime.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button
              onClick={() => navigate('/trials?create=true')}
              className="btn-primary"
              size="sm"
              data-testid="create-trial-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Trial
            </Button>
            <Button
              onClick={() => navigate('/jobs?create=true')}
              variant="outline"
              size="sm"
              data-testid="post-job-btn"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Post Job
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Club Profile Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <Card className="card-premium overflow-hidden border-0">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background flex-shrink-0">
                <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
                <AvatarFallback className="text-lg font-bold bg-primary text-primary-foreground">
                  {currentUser.name?.[0] || 'C'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-foreground truncate">{currentUser.name}</h2>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <Trophy className="w-3 h-3 mr-1.5 text-primary" />
                        {currentUser.sports?.[0] || 'Multi-Sport'}
                      </span>
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <MapPin className="w-3 h-3 mr-1.5" />
                        {currentUser.location || 'Not specified'}
                      </span>
                      <span className="inline-flex items-center text-xs font-semibold text-foreground bg-primary/10 px-2.5 py-1 rounded-full">
                        <Users className="w-3 h-3 mr-1.5 text-primary" />
                        {analytics.followers || 0} followers
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditStatsModal(true)}
                    className="text-xs h-8 px-3"
                    data-testid="edit-club-stats-btn"
                  >
                    <Edit className="w-3 h-3 mr-1.5" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
        {/* Premium Navigation Cards Grid */}
        <motion.div 
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {/* Trial Management */}
          <motion.div variants={cardVariants}>
            <div 
              className={`stat-card ${activeSection === 'trials' ? 'active' : ''}`}
              onClick={() => setActiveSection('trials')}
              data-testid="trials-nav-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-label">Trial Management</p>
                  <h3 className="stat-value mt-1">{(trials.upcoming?.length || 0) + (trials.ongoing?.length || 0)}</h3>
                </div>
                <div className="icon-container-purple">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Trial Applications */}
          <motion.div variants={cardVariants}>
            <div 
              className={`stat-card ${activeSection === 'applicants' ? 'active' : ''}`}
              onClick={() => setActiveSection('applicants')}
              data-testid="trial-applications-nav-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-label">Trial Applications</p>
                  <h3 className="stat-value mt-1">{applications.total || 0}</h3>
                </div>
                <div className="icon-container-blue">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Job Management */}
          <motion.div variants={cardVariants}>
            <div 
              className={`stat-card ${activeSection === 'jobmanagement' ? 'active' : ''}`}
              onClick={() => setActiveSection('jobmanagement')}
              data-testid="job-management-nav-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-label">Job Management</p>
                  <h3 className="stat-value mt-1">{myJobs.length || 0}</h3>
                </div>
                <div className="icon-container-green">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Job Applications */}
          <motion.div variants={cardVariants}>
            <div 
              className={`stat-card ${activeSection === 'jobapps' ? 'active' : ''}`}
              onClick={() => setActiveSection('jobapps')}
              data-testid="job-applications-nav-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-label">Job Applications</p>
                  <h3 className="stat-value mt-1">{jobAppStats.total || 0}</h3>
                </div>
                <div className="icon-container-orange">
                  <Briefcase className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enquiries */}
          <motion.div variants={cardVariants}>
            <div 
              className={`stat-card ${activeSection === 'enquiries' ? 'active' : ''}`}
              onClick={() => setActiveSection('enquiries')}
              data-testid="enquiries-nav-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-label">Enquiries</p>
                  <h3 className="stat-value mt-1">{enquiries.length || 0}</h3>
                </div>
                <div className="icon-container-pink">
                  <MessageCircle className="w-5 h-5 text-purple-300" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Engagement */}
          <motion.div variants={cardVariants}>
            <div 
              className="stat-card"
              onClick={() => setShowEngagementModal(true)}
              data-testid="engagement-nav-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-label">Engagement</p>
                  <h3 className="stat-value mt-1">{analytics.engagement || 0}</h3>
                </div>
                <div className="icon-container bg-gradient-to-br from-purple-400/10 to-purple-600/10">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Hidden TabsList for controlled tabs */}
        <TabsList className="hidden">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trials">Trials</TabsTrigger>
          <TabsTrigger value="applicants">Trial Applications</TabsTrigger>
          <TabsTrigger value="jobapps">Job Applications</TabsTrigger>
          <TabsTrigger value="jobmanagement">Job Management</TabsTrigger>
          <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Trial Analytics */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    Trial Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Total Trials</span>
                      <span className="text-sm font-semibold flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1.5 text-primary/60" />
                        {analytics.totalTrials || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Total Views</span>
                      <span className="text-sm font-semibold flex items-center">
                        <Eye className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                        {analytics.totalTrialViews || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Applications</span>
                      <span className="text-sm font-semibold">{applications.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Conversion Rate</span>
                      <span className="text-sm font-semibold">{analytics.conversionRate || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm font-medium">Pending Review</span>
                      <span className="badge-primary">{applications.pending || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Reputation & Network */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Award className="w-4 h-4 text-primary" />
                    Reputation & Network
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  <div className="space-y-4">
                    {/* Total Followers */}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Total Followers</span>
                      <span className="text-xl font-bold text-primary">{analytics.followers || 0}</span>
                    </div>
                    
                    {/* Follower Breakdown */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="text-center p-2 bg-primary/10 rounded-lg">
                        <p className="text-sm font-bold text-primary">{analytics.followerBreakdown?.athletes || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Athletes</p>
                      </div>
                      <div className="text-center p-2 bg-purple-600/10 rounded-lg">
                        <p className="text-sm font-bold text-purple-600">{analytics.followerBreakdown?.professionals || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Pros</p>
                      </div>
                      <div className="text-center p-2 bg-purple-400/10 rounded-lg">
                        <p className="text-sm font-bold text-purple-400">{analytics.followerBreakdown?.fans || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Fans</p>
                      </div>
                      <div className="text-center p-2 bg-purple-500/10 rounded-lg">
                        <p className="text-sm font-bold text-purple-500">{analytics.followerBreakdown?.clubs || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Clubs</p>
                      </div>
                    </div>
                    
                    {/* Post Engagement */}
                    <div className="pt-3 border-t border-border/50">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5 text-sm">
                            <Heart className="w-4 h-4 text-primary" />
                            <span className="font-semibold">{analytics.totalLikes || 0}</span>
                          </span>
                          <span className="flex items-center gap-1.5 text-sm">
                            <MessageCircle className="w-4 h-4 text-purple-400" />
                            <span className="font-semibold">{analytics.totalComments || 0}</span>
                          </span>
                        </div>
                        <Button 
                          size="sm"
                          variant="ghost" 
                          className="text-xs h-8"
                          onClick={() => navigate('/discover')}
                        >
                          Explore <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </TabsContent>

        {/* Trial Management Tab */}
        <TabsContent value="trials" className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveSection('overview')}
            className="mb-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Overview
          </Button>
          
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="icon-container-purple">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-lg font-bold">Trial Management</span>
                </div>
                <Button onClick={() => navigate('/trials?create=true')} className="btn-primary" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Create Trial
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="upcoming" className="w-full">
                <TabsList className="w-full sm:w-auto mb-4 bg-muted/50">
                  <TabsTrigger value="upcoming" className="text-xs sm:text-sm">
                    Upcoming ({trials.upcoming?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="ongoing" className="text-xs sm:text-sm">
                    Ongoing ({trials.ongoing?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="closed" className="text-xs sm:text-sm">
                    Closed ({trials.closed?.length || 0})
                  </TabsTrigger>
                </TabsList>

                {['upcoming', 'ongoing', 'closed'].map(status => (
                  <TabsContent key={status} value={status}>
                    {trials[status]?.length > 0 ? (
                      <div className="space-y-3">
                        {trials[status].map((trial) => (
                          <div 
                            key={trial._id}
                            className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-all cursor-pointer hover-lift"
                            onClick={() => navigate(`/trials/${trial._id}`)}
                          >
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-foreground mb-2">{trial.title}</h4>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                  <span className="flex items-center">
                                    <MapPin className="w-3 h-3 mr-1" /> {trial.location}
                                  </span>
                                  <span className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" /> {trial.date}
                                  </span>
                                  <span className="flex items-center">
                                    <Trophy className="w-3 h-3 mr-1" /> {trial.sport}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="badge-neutral">{trial.applicants} Applicants</span>
                                <span className={getStatusBadgeClass(trial.status)}>{trial.status}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={(e) => handleDeleteTrial(trial._id, e)}
                                  data-testid={`delete-trial-${trial._id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                        <p className="text-muted-foreground">No {status} trials</p>
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trial Applications Tab */}
        <TabsContent value="applicants" className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveSection('overview')}
            className="mb-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Overview
          </Button>
          
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="icon-container-blue">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-lg font-bold">Trial Applications</span>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <select
                    className="input-premium text-sm flex-1 sm:flex-none sm:w-40"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="applied">Applied</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Trial Filter Tabs */}
              <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-border/50">
                <Button
                  size="sm"
                  variant={selectedTrialTab === 'all' ? 'default' : 'outline'}
                  className={selectedTrialTab === 'all' ? 'btn-primary' : ''}
                  onClick={() => setSelectedTrialTab('all')}
                >
                  All Trials ({applicants.length})
                </Button>
                {trialsList.map((trial) => (
                  <Button
                    key={trial._id}
                    size="sm"
                    variant={selectedTrialTab === trial._id ? 'default' : 'outline'}
                    className={selectedTrialTab === trial._id ? 'btn-primary' : ''}
                    onClick={() => setSelectedTrialTab(trial._id)}
                  >
                    {trial.title} ({trial.applicationCount})
                  </Button>
                ))}
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Applied', value: applicants.filter(a => a.status === 'applied').length, color: 'purple' },
                  { label: 'Shortlisted', value: applicants.filter(a => a.status === 'shortlisted').length, color: 'purple' },
                  { label: 'Accepted', value: applicants.filter(a => a.status === 'accepted').length, color: 'purple' },
                  { label: 'Rejected', value: applicants.filter(a => a.status === 'rejected').length, color: 'purple' }
                ].map((stat) => (
                  <div key={stat.label} className={`text-center p-3 rounded-xl bg-primary/10`}>
                    <p className={`text-xl font-bold text-primary`}>{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Applications List */}
              {loadingApplicants ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-muted/30 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : applicants.length > 0 ? (
                <div className="space-y-3">
                  {applicants.map((app) => (
                    <div 
                      key={app._id}
                      className="p-4 rounded-xl bg-muted/20 hover:bg-muted/40 transition-all cursor-pointer hover-lift border border-transparent hover:border-primary/20"
                      onClick={() => {
                        setSelectedTrialApp(app);
                        setTrialAppDetailModal(true);
                      }}
                      data-testid={`trial-application-${app._id}`}
                    >
                      <div className="flex items-start gap-4">
                        <Avatar 
                          className="w-12 h-12 ring-2 ring-primary/20"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/profile/${app.athlete.username}`);
                          }}
                        >
                          <AvatarImage src={app.athlete.profileImage} alt={app.athlete.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                            {app.athlete.name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-semibold text-foreground">{app.athlete.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                Applied for: <span className="font-medium text-foreground">{app.trialTitle}</span>
                              </p>
                            </div>
                            <span className={getStatusBadgeClass(app.status)}>{app.status}</span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setSelectedTrialApp(app); setTrialAppDetailModal(true); }}>
                              <Eye className="w-3 h-3 mr-1" /> View
                            </Button>
                            {app.status !== 'accepted' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-primary border-primary/30" onClick={(e) => { e.stopPropagation(); handleTrialAppStatusUpdate(app._id, 'accepted'); }}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Accept
                              </Button>
                            )}
                            {app.status !== 'rejected' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-destructive border-destructive/30" onClick={(e) => { e.stopPropagation(); handleTrialAppStatusUpdate(app._id, 'rejected'); }}>
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No applications received yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Job Applications Tab */}
        <TabsContent value="jobapps" className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveSection('overview')}
            className="mb-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Overview
          </Button>
          
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="icon-container-orange">
                    <Briefcase className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-lg font-bold">Job Applications</span>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <select
                    className="input-premium text-sm flex-1 sm:flex-none sm:w-40"
                    value={jobAppFilter}
                    onChange={(e) => setJobAppFilter(e.target.value)}
                  >
                    <option value="">All Applications</option>
                    <option value="applied">Applied ({jobAppStats.applied})</option>
                    <option value="shortlisted">Shortlisted ({jobAppStats.shortlisted})</option>
                    <option value="accepted">Accepted ({jobAppStats.accepted})</option>
                    <option value="rejected">Rejected ({jobAppStats.rejected})</option>
                  </select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-3 mb-6 p-4 bg-muted/30 rounded-xl">
                {[
                  { label: 'Total', value: jobAppStats.total, color: 'foreground' },
                  { label: 'Applied', value: jobAppStats.applied, color: 'primary' },
                  { label: 'Shortlisted', value: jobAppStats.shortlisted, color: 'primary' },
                  { label: 'Accepted', value: jobAppStats.accepted, color: 'primary' }
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className={`text-xl font-bold ${stat.color === 'foreground' ? 'text-foreground' : 'text-primary'}`}>{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Applications List */}
              {loadingJobApplications ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-muted/30 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : jobApplications.length > 0 ? (
                <div className="space-y-3">
                  {jobApplications.map((app) => (
                    <div 
                      key={app._id}
                      className="p-4 rounded-xl bg-muted/20 hover:bg-muted/40 transition-all cursor-pointer hover-lift border border-transparent hover:border-primary/20"
                      onClick={() => {
                        setSelectedJobApp(app);
                        setJobAppDetailModal(true);
                      }}
                      data-testid={`job-application-${app._id}`}
                    >
                      <div className="flex items-start gap-4">
                        <Avatar className="w-12 h-12 ring-2 ring-purple-400/20">
                          <AvatarImage src={app.applicant?.profileImage} alt={app.applicant?.name} />
                          <AvatarFallback className="bg-purple-400/10 text-purple-400 font-semibold">
                            {app.applicant?.name?.[0] || 'A'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-semibold text-foreground">{app.applicant?.name || 'Unknown'}</h4>
                              <p className="text-sm text-muted-foreground">
                                Applied for: <span className="font-medium text-foreground">{app.jobTitle}</span>
                              </p>
                            </div>
                            <span className={getStatusBadgeClass(app.status)}>
                              {app.status === 'pending' ? 'Applied' : app.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setSelectedJobApp(app); setJobAppDetailModal(true); }}>
                              <Eye className="w-3 h-3 mr-1" /> View
                            </Button>
                            {app.cvUrl && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); window.open(app.cvUrl, '_blank'); }}>
                                <FileText className="w-3 h-3 mr-1" /> CV
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground mb-4">No job applications yet</p>
                  <Button onClick={() => navigate('/jobs?create=true')} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" /> Post Job
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Job Management Tab */}
        <TabsContent value="jobmanagement" className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveSection('overview')}
            className="mb-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Overview
          </Button>
          
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="icon-container-green">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-lg font-bold">Job Management</span>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <select
                    className="input-premium text-sm flex-1 sm:flex-none sm:w-32"
                    value={jobFilter}
                    onChange={(e) => setJobFilter(e.target.value)}
                  >
                    <option value="">All Jobs</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                  <Button onClick={() => navigate('/jobs?create=true')} className="btn-primary" size="sm">
                    <Plus className="w-4 h-4 mr-2" /> New Job
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMyJobs ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : myJobs.length > 0 ? (
                <div className="space-y-4">
                  {myJobs.map((job) => (
                    <div 
                      key={job._id}
                      className="p-5 rounded-xl bg-muted/20 hover:bg-muted/30 transition-all border border-transparent hover:border-primary/20"
                      data-testid={`job-item-${job._id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <h4 className="font-bold text-lg text-foreground">{job.title}</h4>
                            <span className={getStatusBadgeClass(job.status)}>{job.status}</span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
                            {job.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" /> {job.location}
                              </span>
                            )}
                            {job.employmentType && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3.5 h-3.5" /> {job.employmentType}
                              </span>
                            )}
                            {job.salary && (
                              <span className="flex items-center gap-1">£ {job.salary}</span>
                            )}
                          </div>
                          
                          {/* Application Stats Badges */}
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-xs">Total: {job.applicationStats?.total || 0}</Badge>
                            <Badge variant="outline" className="text-xs text-primary border-primary/30">Applied: {job.applicationStats?.applied || 0}</Badge>
                            <Badge variant="outline" className="text-xs text-purple-600 border-purple-600/30">Accepted: {job.applicationStats?.accepted || 0}</Badge>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 lg:flex-col">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${job._id}`)}>
                            <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedJob(job); setEditJobModal(true); }} data-testid={`edit-job-${job._id}`}>
                            <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-destructive border-destructive/30 hover:bg-destructive/10"
                            onClick={() => handleDeleteJob(job._id)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground mb-4">No jobs posted yet</p>
                  <Button onClick={() => navigate('/jobs?create=true')} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" /> Post First Job
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enquiries Tab */}
        <TabsContent value="enquiries" className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveSection('overview')}
            className="mb-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Overview
          </Button>
          
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="icon-container-pink">
                    <Mail className="w-5 h-5 text-purple-300" />
                  </div>
                  <span className="text-lg font-bold">Enquiries</span>
                </div>
                <select
                  className="input-premium text-sm w-full sm:w-40"
                  value={enquiryFilter}
                  onChange={(e) => setEnquiryFilter(e.target.value)}
                >
                  <option value="all">All Enquiries</option>
                  <option value="pending">Pending</option>
                  <option value="responded">Responded</option>
                </select>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingEnquiries ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-muted/30 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : enquiries.length > 0 ? (
                <div className="space-y-3">
                  {enquiries.map((enquiry) => (
                    <div 
                      key={enquiry._id}
                      className={`p-4 rounded-xl transition-all cursor-pointer hover-lift border ${
                        !enquiry.read 
                          ? 'bg-primary/5 border-primary/20' 
                          : 'bg-muted/20 border-transparent hover:border-primary/20'
                      }`}
                      onClick={() => {
                        setSelectedEnquiry(enquiry);
                        setEnquiryDetailModal(true);
                        if (!enquiry.read) handleMarkEnquiryRead(enquiry._id);
                      }}
                      data-testid={`enquiry-${enquiry._id}`}
                    >
                      <div className="flex items-start gap-4">
                        <Avatar className="w-12 h-12 ring-2 ring-purple-300/20">
                          <AvatarImage src={enquiry.enquirer?.profileImage} alt={enquiry.enquirer?.name} />
                          <AvatarFallback className="bg-purple-300/10 text-purple-400 font-semibold">
                            {enquiry.enquirer?.name?.[0] || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-semibold text-foreground">{enquiry.enquirer?.name || 'Unknown'}</h4>
                            <div className="flex items-center gap-2">
                              {!enquiry.read && <span className="badge-primary text-[10px]">New</span>}
                              <span className={getStatusBadgeClass(enquiry.status)}>{enquiry.status}</span>
                            </div>
                          </div>
                          <p className="font-medium text-sm text-foreground mb-1">{enquiry.subject}</p>
                          <p className="text-sm text-muted-foreground line-clamp-2">{enquiry.message}</p>
                          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(enquiry.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No enquiries yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <JobApplicationDetailModal
        isOpen={jobAppDetailModal}
        onClose={setJobAppDetailModal}
        application={selectedJobApp}
        onStatusUpdate={handleJobAppStatusUpdate}
        onNavigate={navigate}
      />

      <TrialApplicationDetailModal
        isOpen={trialAppDetailModal}
        onClose={setTrialAppDetailModal}
        application={selectedTrialApp}
        onStatusUpdate={handleTrialAppStatusUpdate}
        onNavigate={navigate}
      />

      <EditJobModal
        isOpen={editJobModal}
        onClose={setEditJobModal}
        job={selectedJob}
        onSave={handleUpdateJob}
      />

      <EnquiryDetailModal
        isOpen={enquiryDetailModal}
        onClose={setEnquiryDetailModal}
        enquiry={selectedEnquiry}
        onStatusUpdate={handleEnquiryStatusUpdate}
        onNavigate={navigate}
      />

      <EditStatsModal
        isOpen={editStatsModal}
        onClose={setEditStatsModal}
        stats={editableStats}
        onSave={handleSaveStats}
      />

      <EngagementAnalytics 
        isOpen={showEngagementModal} 
        onClose={() => setShowEngagementModal(false)}
      />
    </div>
  );
};

export default ClubDashboard;
